<template>
	<SimpleModal
		@close="$emit('close')"
		fullscreen
		class="image-modal"
		:tabOrder="tabOrder"
	>
		<div
			class="image-modal-container"
			:class="{ hovered: isHovered }"
			@mousemove="hoverOn"
		>
			<div
				v-if="hasMultiple"
				class="gallery-scroll-left"
				@click="selectImage(-1)"
			>
				<ChevronIcon type="regular" left />
			</div>
			<div
				v-if="hasMultiple"
				class="gallery-scroll-right"
				@click="selectImage(1)"
			>
				<ChevronIcon type="regular" right />
			</div>
			<div
				class="image-modal-header solid"
				ref="header"
				:class="{
					'image-modal-header-access': accessibilityMode,
					'has-count': hasMultiple,
				}"
			>
				<div class="media-count">
					<div v-if="hasMultiple">
						{{ currentPosition + 1 }}/{{ mediaForDisplay.length }}
					</div>
				</div>
				<div class="image-modal-header-actions">
					<div v-if="store.config.wrapper" class="popout" @click="handlePopout">
						<ArrowUpRightFromSquareIcon class="popout-icon" />
					</div>
					<a :href="downloadUrl" class="download" :download="downloadFileName">
						<ArrowFromBracketIcon class="download-icon" down />
					</a>
					<div class="vertical-line" />
				</div>
			</div>
			<div class="fullscreen-media">
				<slot>
					<img
						class="media"
						v-if="imageUrl || currentMedia.isPhoto"
						:src="imageUrl || currentMedia.mediaUrl"
						data-private
						@load="onLoad"
					/>
					<video
						class="media video"
						:class="{ loading: isLoading }"
						v-if="videoUrl || currentMedia.isVideo"
						controls
						autoplay
						:src="videoUrl || currentMedia.mediaUrl"
						data-private
						@canplay="onLoad"
					/>
					<object
						class="media pdf"
						v-if="pdfInlineUrl"
						@load="onLoad"
						@mousemove="hoverOn"
						data-private
						:data="pdfInlineUrl"
					/>
				</slot>

				<div v-if="isLoading" class="loading-indicator">
					<PhotoFilmIcon type="solid" />
					<Spinner v-if="isLoading" />
					<div class="loading-text">
						{{ $t('common_loader_loading_title') }}
					</div>
				</div>
			</div>
			<div
				v-if="hasMultiple"
				class="image-gallery"
				:class="{ 'is-hovered': isHovered }"
			>
				<div
					v-for="(media, i) in mediaForDisplay"
					:key="media.id"
					:class="{
						'highlighted-thumbnail': currentPosition === i,
						file: media.isFile,
					}"
					class="gallery-item"
					@click="focusMedia(media)"
				>
					<img
						v-if="media.isVisual"
						:src="media.thumbnailUrl || media.mediaUrl"
						:ref="`media_${media.id}`"
						class="gallery-thumbnail"
					/>
					<FileIcon
						v-if="!media.isVisual"
						class="gallery-file-icon"
						:icon="media.fileIcon"
					/>
					<div v-if="media.isGif" class="gif-icon gif-label">
						<GifTextIcon />
					</div>
					<div v-if="media.isVideo" class="play-icon">
						<PlayIcon type="solid" />
					</div>
				</div>
			</div>
		</div>
	</SimpleModal>
</template>

<script>
import store from '@/store'
import SimpleModal from './SimpleModal'
import FileIcon from '@/components/common/FileIcon'
import Spinner from '@/components/common/Spinner'
import {
	ChevronIcon,
	ArrowFromBracketIcon,
	ArrowUpRightFromSquareIcon,
	GifTextIcon,
	PlayIcon,
	PhotoFilmIcon,
} from '@/icons'
import { GifPlayer } from '@/helpers/gif'
import { openPopout, safeUrl } from '@/helpers/utils'

export default {
	name: 'ImageModal',
	components: {
		SimpleModal,
		ChevronIcon,
		ArrowFromBracketIcon,
		ArrowUpRightFromSquareIcon,
		GifTextIcon,
		PlayIcon,
		PhotoFilmIcon,
		Spinner,
		FileIcon,
	},
	props: {
		imageUrl: String,
		videoUrl: String,
		pdfUrl: String,
		downloadName: String,
		mediaItems: {
			type: Array,
			default: () => [],
		},
		mediaId: Number,
	},
	emits: ['close', 'update:imageUrl', 'update:videoUrl'],
	data() {
		return {
			store,
			currentPosition: 0,
			isHovered: true,
			isLoading: true,
		}
	},

	created() {
		if (this.mediaId && this.mediaForDisplay?.length) {
			const mediaIndex = this.mediaForDisplay.findIndex(
				(item) => (item.id || item.tempId) === this.mediaId
			)
			this.currentPosition = mediaIndex === -1 ? 0 : mediaIndex
			this.focusMedia(this.mediaForDisplay[this.currentPosition])
		}
	},

	async mounted() {
		await this.$nextTick()
		this.pauseGifs()
		this.hoverOn()

		window.addEventListener('keydown', this.handleArrowKeyPress)
	},

	beforeUnmount() {
		window.removeEventListener('keydown', this.handleArrowKeyPress)
	},

	computed: {
		mediaForDisplay() {
			return (this.mediaItems ?? []).filter((m) => m.isVisual || m.isPDF)
		},

		tabOrder() {
			const tabs = [
				{ css: '.download', clickOnEnter: true },
				{ css: '.image-modal-container .accessible-item' },
			]
			if (this.hasMultiple) {
				const onEscape = (vm, item, e) => {
					e.stopPropagation()
					return null
				}
				tabs.push({
					all: '.gallery-item',
					clickOnEnter: true,
					stop: true,
					Escape: onEscape,
				})
			}
			return tabs
		},
		downloadUrl() {
			let url =
				this.currentMedia?.mediaUrl ||
				this.videoUrl ||
				this.imageUrl ||
				this.pdfUrl ||
				null

			if (!url) return null
			if (!url.startsWith(location.origin) && !url.startsWith('data:')) {
				try {
					const tempUrl = new URL(url)
					url = location.origin + tempUrl.pathname
				} catch {
					// a schemeless or malformed payload url must not break the modal
					return null
				}
				if (store.auth.media_token?.accessToken) {
					url += `?t=${store.auth.media_token.accessToken}`
				}
			}
			// data: is only trusted for locally generated images (GroupInvites
			// renders the QR code via canvas toDataURL) — the imageUrl/videoUrl
			// props are otherwise payload-derived through VisualMedia → MediaModal
			const extraSchemes = /^data:image\//.test(url) ? ['data:'] : []
			return safeUrl(url, extraSchemes) || null
		},
		downloadFileName() {
			const name = this.downloadName || this.currentMedia?.title || null
			if (name) return name
			if (this.downloadUrl) {
				if (this.currentMedia?.urlmimetype) {
					const ext = this.currentMedia.urlmimetype.split('/')[1]
					return `pronto_download.${ext}`
				}
				return 'pronto_download'
			}
			return null
		},
		currentMedia() {
			return this.mediaForDisplay[this.currentPosition] || {}
		},
		hasMultiple() {
			return this.mediaForDisplay.length > 1
		},
		pdfInlineUrl() {
			if (this.pdfUrl || this.currentMedia.isPDF) {
				const downloadUrl = this.pdfUrl || this.currentMedia.mediaUrl
				if (!downloadUrl.includes('pronto.io')) return downloadUrl
				const url = new URL(downloadUrl)
				url.searchParams.set('inline', '1')
				return url.toString()
			}
			return null
		},
	},

	methods: {
		handlePopout() {
			const url = new URL(this.downloadUrl)
			openPopout(url.pathname)
		},
		focusMedia(media) {
			if (!media) {
				this.currentPosition = 0
				return
			}
			const mediaIndex = this.mediaForDisplay.findIndex(
				(item) => (item.id || item.tempId) === (media.id || media.tempId)
			)
			this.currentPosition = mediaIndex === -1 ? 0 : mediaIndex
			this.hoverOn()
		},

		selectImage(offsetIndex) {
			this.currentPosition += offsetIndex
			if (this.currentPosition < 0) {
				// if we've tried to move too far left, wrap around to end
				this.currentPosition = this.mediaForDisplay.length - 1
			} else if (this.currentPosition > this.mediaForDisplay.length - 1) {
				// if we've tried to move too far right, wrap around to start
				this.currentPosition = 0
			}
			this.hoverOn()
			setTimeout(() => this.scrollMediaIntoView(), 25)
		},

		scrollMediaIntoView() {
			const elem = document.querySelector('.highlighted-thumbnail')
			if (elem) {
				elem.scrollIntoView({ behavior: 'smooth' })
			}
		},

		pauseGifs() {
			if (!this.mediaForDisplay.length) return
			this.mediaForDisplay
				.filter((m) => m.isGif)
				.forEach((media) => {
					const refName = `media_${media.id}`
					if (this.$refs[refName]) {
						new GifPlayer(this.$refs[refName][0], {
							play: false,
						})
					}
				})
		},

		handleArrowKeyPress(event) {
			switch (event.code) {
				case 'ArrowRight':
					this.selectImage(1)
					break
				case 'ArrowLeft':
					this.selectImage(-1)
					break
			}
		},

		hoverOn() {
			this.isHovered = true
			if (this.accessibilityMode) return true
			clearTimeout(this.hoverTimeout)
			this.hoverTimeout = setTimeout(() => {
				this.isHovered = false
			}, 3000)
		},

		onLoad() {
			this.isLoading = false
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

$header-height: 60px;

.image-modal-container {
	&.hovered {
		.image-modal-header,
		.image-gallery,
		.gallery-scroll-left,
		.gallery-scroll-right {
			opacity: 1;
		}
	}
	.image-modal-header,
	.image-gallery,
	.gallery-scroll-left,
	.gallery-scroll-right {
		opacity: 0;
		transition: opacity ease-in-out 0.3s;
	}
}

.image-modal-header {
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	height: $header-height;
	@include flex;
	justify-content: center;
	z-index: 3;
	&.has-count {
		background-color: rgba(0, 0, 0, 0.7);
	}
	.media-count {
		font-family: Avenir-Heavy;
		color: white;
	}
	.image-modal-header-actions {
		position: absolute;
		top: 0;
		right: 66px;
		height: $header-height;
		@include flex(16px);

		.popout {
			height: 22px;
			cursor: pointer;
			.popout-icon {
				color: white;
				font-size: 22px;
			}
		}
		.download {
			height: 22px;
			cursor: pointer;
			.download-icon {
				color: white;
				font-size: 22px;
			}
		}
	}
}

.image-modal-header-access {
	opacity: 1;
	transition: none;
}

.fullscreen-media {
	display: flex;
	align-items: center;
	justify-content: center;
	height: 100vh;
	width: 100vw;
	background: black;
	position: relative;
	padding-top: 60px;
	padding-bottom: 90px;
	.media {
		max-height: 100%;
		max-width: 100%;
		z-index: 2;

		&.video {
			&.loading {
				opacity: 0;
			}
		}

		&.pdf {
			width: 100%;
			margin-top: $header-height;
			height: calc(100vh - #{$header-height});
		}
	}
}

.vertical-line {
	height: 24px;
	border-left: 1px solid var(--neutral-700);
}

.image-gallery {
	@include flex(8px);
	overflow-y: hidden;
	overflow-x: auto;
	padding: 15px;
	background-color: rgba(0, 0, 0, 0.7);
	position: absolute;
	bottom: 0;
	left: 0;
	right: 0;
	z-index: 5;
	max-width: max-content;
	margin: 0 auto;
}

.gallery-item {
	cursor: pointer;
	position: relative;
	width: 60px;
	height: 60px;
	border-radius: 4px;
	background-color: #353535;
	flex-shrink: 0;
	border: 2px solid transparent;
	&.file {
		@include flex;
		justify-content: center;
	}
	&.highlighted-thumbnail {
		border-color: var(--teal-300);
		.gallery-thumbnail {
			border-radius: 0px;
		}
	}

	.play-icon {
		position: absolute;
		top: 0;
		left: 0;
		bottom: 0;
		right: 0;
		pointer-events: none;
		background-color: rgba(0, 0, 0, 0.4);
		.pronto-icon {
			display: flex;
			justify-content: center;
			align-items: center;
			font-size: 22px;
			color: var(--neutral-0);
			width: 100%;
			height: 100%;
		}
	}
	.gallery-file-icon {
		font-size: 42px;
	}
}
.gallery-thumbnail {
	width: 100%;
	height: 100%;
	transition: transform 0.2s;
	object-fit: cover;
	object-position: center;
	border-radius: 4px;
}

.gallery-item:hover {
	transform: scale(1.05);
}
.gallery-scroll-left,
.gallery-scroll-right {
	position: absolute;
	top: 50%; /* Center vertically */
	transform: translateY(-50%);
	background-color: rgba(0, 0, 0, 0.5); /* Semi-transparent background */
	color: white;
	border: none;
	cursor: pointer;
	z-index: 20; /* Ensure it's above other content */
}

.gallery-scroll-left,
.gallery-scroll-right {
	height: 40px;
	width: 40px;
	border-radius: 50%;
	background-color: var(--text-gray-dark);
	display: flex;
	justify-content: center;
	align-items: center;
}

.gallery-scroll-left {
	left: 10px; /* Position to the far left */
}

.gallery-scroll-right {
	right: 10px; /* Position to the far right */
}

.gif-icon {
	position: absolute;
	display: flex;
	align-items: center;
	justify-content: center;
	background: rgba(0, 0, 0, 0.65);
	width: 36px;
	height: 29px;
	z-index: 2;
	top: 0;
	font-size: 17px;
	&.gif-label {
		top: 0;
		left: 0;
		border-bottom-right-radius: 2px;
	}
}

.loading-indicator {
	position: absolute;
	background-color: #1a1a1a;
	height: 75%;
	width: 75%;
	display: flex;
	justify-content: center;
	align-items: center;
	flex-direction: column;
	z-index: 1;
	.pronto-icon {
		font-size: 114px;
	}

	.pronto-spinner {
		margin-top: 100px;
	}

	.loading-text {
		font-size: 14px;
		color: var(--neutral-200);
		margin-top: 10px;
	}
}
</style>
<style lang="scss">
.image-modal {
	.modal-close-btn {
		font-size: 28px;
		color: white;
		top: 15px;
		right: 25px;
		z-index: 4;
		&:hover {
			~ .image-modal-header,
			~ .fullscreen-media > .media-count,
			~ .image-gallery {
				opacity: 1 !important;
			}
		}
	}

	.gallery-item {
		.gif-player {
			height: 60px;
			width: 60px;
		}
		&.highlighted-thumbnail {
			.gif-player-canvas {
				height: 56px !important;
				width: 56px !important;
			}
		}
	}
}
</style>
